import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { useOrigins, useRegions } from "@/hooks/useReferenceData";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

interface RegionManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegionManagementDialog({ open, onOpenChange }: RegionManagementDialogProps) {
  useAppLocale();
  const regions = useRegions();
  const origins = useOrigins();
  const createRegion = useMutation(api.products.createRegion);
  const updateRegion = useMutation(api.products.updateRegion);
  const deleteRegion = useMutation(api.products.deleteRegion);

  const [search, setSearch] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [originId, setOriginId] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const originMap = React.useMemo(() => {
    const map = new Map<string, string>();
    origins?.forEach((o: any) => map.set(o._id, o.name));
    return map;
  }, [origins]);

  const filtered = React.useMemo(() => {
    if (!regions) return [];
    if (!search) return regions;
    const q = search.toLowerCase();
    return regions.filter((r: any) => {
      const rName = r.name?.toLowerCase() ?? "";
      const oName = originMap.get(r.originId)?.toLowerCase() ?? "";
      return rName.includes(q) || oName.includes(q);
    });
  }, [regions, search, originMap]);

  const originOptions = React.useMemo(
    () => (origins ?? []).map((origin: any) => ({ value: origin._id, label: origin.name })),
    [origins],
  );

  const resetForm = () => {
    setName("");
    setDescription("");
    setOriginId("");
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(m.name_required_simple());
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateRegion({
          id: editingId as any,
          name: name.trim(),
          description: description || undefined,
          originId: originId || undefined,
        });
        toast.success(m.region_updated());
      } else {
        await createRegion({
          name: name.trim(),
          description: description || undefined,
          originId: originId || undefined,
        });
        toast.success(m.region_created());
      }
      resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to save region", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (region: any) => {
    setEditingId(region._id);
    setName(region.name);
    setDescription(region.description ?? "");
    setOriginId(region.originId ?? "");
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRegion({ id: id as any });
      toast.success(m.region_deleted());
      if (editingId === id) resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to delete region", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{m.manage_regions()}</DialogTitle>
          <DialogDescription>{m.manage_regions_description()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid gap-2">
              <Label>{editingId ? m.edit_region() : m.add_region()}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.region_name()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.description()}</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={m.optional_description()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.origin()}</Label>
              <ComboboxSelect
                options={originOptions}
                value={originId}
                onValueChange={setOriginId}
                placeholder={m.select_origin()}
                searchPlaceholder="Search origins..."
                emptyText="No origins found."
                loading={origins === undefined}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
                {saving ? m.saving() : editingId ? m.update() : m.add()}
              </Button>
              {editingId && (
                <Button size="sm" variant="outline" onClick={resetForm}>{m.cancel()}</Button>
              )}
            </div>
          </div>

          <Input placeholder={m.search_regions()} value={search} onChange={(e) => setSearch(e.target.value)} />

          <div className="max-h-[300px] space-y-1 overflow-y-auto">
            {filtered.map((region: any) => (
              <div
                key={region._id}
                className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
              >
                <div className="flex items-center gap-2">
                  <div>
                    <p className="text-sm font-medium">{region.name}</p>
                    {region.description && (
                      <p className="text-xs text-muted-foreground">{region.description}</p>
                    )}
                  </div>
                  {region.originId && originMap.has(region.originId) && (
                    <Badge variant="outline" className="text-xs">{originMap.get(region.originId)}</Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon-xs" variant="ghost" onClick={() => handleEdit(region)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon-xs" variant="ghost" aria-label={`Delete ${region.name}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete region?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove <span className="font-medium">{region.name}</span> from the catalog. Producers tied to this region will need to be reassigned. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          variant="destructive"
                          onClick={() => void handleDelete(region._id)}
                        >
                          Delete region
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">{m.no_regions_found()}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
